import {
  CustomButton,
  CustomImage,
  CustomModal,
  successDialog,
  Text,
  useText,
} from '@src/components';
import modalClose from '@src/assets/icons/icon-close.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { Radio } from 'antd';
import '@src/styles/manageMyCards/qrMobileModal.scss';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { cardGetPagedList, mobileUpdatePayments } from '@src/store/slice/manageMyCardsSlice';
import { VouIdCode } from '@src/store/enums';

const buildUpdateRequest = (cards, selectedValue, vouId) =>
  cards.reduce((acc, card) => {
    const canEnable = card.gsm && !card.hasMobilePayment && selectedValue;
    const canDisable = !card.cardType && card.hasMobilePayment && !selectedValue;

    if (canEnable || canDisable) {
      acc.push({
        customerId: card.customerId,
        vouId,
        hasMobilePayment: selectedValue,
        mobileAppUserId: null,
        cardNumber: card.cardNumber,
      });
    }

    return acc;
  }, []);

const getCardStats = (cards) => ({
  physicalCard: cards.filter((q) => !q.cardType).length,
  notGsmNumberList: cards.filter((q) => !q.gsm),
  hasMobilePaymentPiece: cards.filter((q) => q.hasMobilePayment).length,
  hasNotMobilePaymentPiece: cards.filter((q) => !q.hasMobilePayment).length,
  digitalWithPayment: cards.filter((q) => q.hasMobilePayment && q.cardType).length,
});

const renderSubmitText = ({ selectedValue, stats, selectedCards }) => {
  const {
    notGsmNumberList,
    hasMobilePaymentPiece,
    hasNotMobilePaymentPiece,
    physicalCard,
    digitalWithPayment,
  } = stats;

  if (selectedValue) {
    if (notGsmNumberList.length > 0) {
      return (
        <>
          <div>
            <Text t="toBeAbleToGiveMobilePaymentPermission" />
          </div>
          {notGsmNumberList.map((card) => (
            <span key={card.cardNumber} className="title">
              <br />
              {card.cardNumber}
            </span>
          ))}
        </>
      );
    }

    if (hasMobilePaymentPiece > 0) {
      return (
        <>
          <Text t="selectedCardsMobilePaymentAlreadyGivenPrefix" />
          <span className="title">{hasMobilePaymentPiece}</span>
          <Text t="selectedCardsMobilePaymentAlreadyGivenMiddle" />
          <span className="title">{hasNotMobilePaymentPiece}</span>
          <Text t="selectedCardsMobilePaymentPermissionWillBeGivenSuffix" />
        </>
      );
    }

    return (
      <>
        <Text t="selectedCardsMobilePaymentWillBeGivenPrefix" />
        <span className="title">{selectedCards.filter((q) => q.gsm).length}</span>
        <Text t="selectedCardsMobilePaymentWillBeGivenSuffix" />
      </>
    );
  }

  if (digitalWithPayment > 0) {
    return (
      <span className="title">
        <Text t="mobilePaymentPermissionCannotBeRemovedForDigital2" />
        <br />
        <br />
      </span>
    );
  }

  if (!hasNotMobilePaymentPiece && physicalCard > 0) {
    return (
      <>
        <Text t="selectedPhysicalCardsMobilePaymentPermissionWillBeRemovedPrefix" />
        <span className="title">{physicalCard}</span>
        <Text t="selectedPhysicalCardsMobilePaymentPermissionWillBeRemovedSuffix" />
        <br />
        <br />
      </>
    );
  }

  return (
    <>
      <Text t="selectedCardsWithoutMobilePaymentPrefix" />
      <span className="title">{hasNotMobilePaymentPiece}</span>
      <Text t="selectedCardsWithoutMobilePaymentMiddle" />
      <span className="title">
        {selectedCards.filter((q) => q.hasMobilePayment && !q.cardType).length}
      </span>
      <Text t="selectedCardsMobilePaymentPermissionWillBeRemovedSuffix" />
    </>
  );
};

const MobilePaymentChangeModal = ({
  modalVisible,
  handleModalVisible,
  isCompliments = false,
  isMarket = false,
}) => {
  const changeMobilePaymentPermission2 = useText('changeMobilePaymentPermission2');
  
  const [selectedValue, setSelectedValue] = useState(null);
  const [sumbit, setSumbit] = useState(false);
  const [modalTitle, setModalTitle] = useState(changeMobilePaymentPermission2);
  const { selectedCards, filterObject } = useSelector((state) => state?.manageMyCards);
  const dispatch = useDispatch();

  const vouId = useMemo(() => {
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [isMarket, isCompliments]);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const addMobilePaymentPermission = useText('addMobilePaymentPermission');
  const removeMobilePaymentPermission = useText('removeMobilePaymentPermission');
  const stats = useMemo(() => getCardStats(selectedCards), [selectedCards]);

  useEffect(() => {
    if (!sumbit) {
      setModalTitle(changeMobilePaymentPermission2);
      return;
    }

    setModalTitle(selectedValue ? addMobilePaymentPermission : removeMobilePaymentPermission);
  }, [selectedValue, sumbit]);

  const handleCancel = () => {
    if (sumbit) {
      setSumbit(false);
      return;
    }

    handleModalVisible(false);
    setSelectedValue(null);
  };

  const handleApproved = async () => {
    const newData = buildUpdateRequest(selectedCards, selectedValue, vouId);

    const action = await dispatch(
      mobileUpdatePayments({ mobilePaymentUpdateRequestDtos: newData }),
    );
    if (!mobileUpdatePayments.fulfilled.match(action)) return;

    successDialog({
      title: <Text t="successfullySent" />,
      message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
      onOk: async () => {
        await dispatch(cardGetPagedList({ body: { vouId }, ...filterObject }));
      },
    });
    setSumbit(false);
    setSelectedValue(null);
    handleModalVisible();
  };
  return (
    <CustomModal
      className="qrmobile-change-modal"
      maskClosable={false}
      footer={false}
      title={modalTitle}
      visible={modalVisible}
      onCancel={handleCancel}
      closeIcon={<CustomImage src={modalClose} />}
    >
      {sumbit ? (
        <div>
          <span>
            {renderSubmitText({
              selectedValue,
              stats,
              selectedCards,
            })}
          </span>

          <div className="footer-btn">
            {stats.digitalWithPayment === 0 && (
              <CustomButton
                id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-cancel-btn"
                className="cancel-btn"
                onClick={() => setSumbit(false)}
              >
                <Text t="giveUp" />
              </CustomButton>
            )}
            <CustomButton
              id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-download-btn"
              className="download-btn"
              onClick={handleApproved}
              disabled={selectedValue && stats.notGsmNumberList.length > 0}
            >
              <Text t="okBtn" />
            </CustomButton>
          </div>
        </div>
      ) : (
        <>
          <span className="title">
            <Text t="pleaseSelectTheTransactionYouWantToPerform" />
          </span>
          <Radio.Group
            id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-custom-radioGroup-input-content"
            onChange={(e) => {
              setSelectedValue(e.target.value);
            }}
            value={selectedValue}
            className="custom-radio-input-content"
          >
            <Radio
              id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-custom-radio-input-contentTrue"
              value={true}
              className="custom-radio-input"
              style={{ marginTop: '20px' }}
            >
              <Text t="giveMobilePaymentPermissionToMySelectedCards" />
            </Radio>
            <Radio
              id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-custom-radio-input-contentFalse"
              value={false}
              className="custom-radio-input"
              style={{ marginTop: '15px' }}
            >
              <Text t="removeMobilePaymentPermissionFromMySelectedCards" />
            </Radio>
          </Radio.Group>
          <div className="footer-btn">
            <CustomButton
              id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-second-cancel-btn"
              className="cancel-btn"
              onClick={() => {
                handleModalVisible(false);
                setSumbit(false);
                setSelectedValue(null);
              }}
            >
              <Text t="giveUp" />
            </CustomButton>
            <CustomButton
              id="ticketRestaurant-manageMyCards-mobilePaymentChangeModal-second-download-btn"
              className="download-btn"
              onClick={() => {
                setSumbit(true);
              }}
              disabled={selectedValue === null}
            >
              <Text t="okBtn" />
            </CustomButton>
          </div>
        </>
      )}
    </CustomModal>
  );
};

MobilePaymentChangeModal.propTypes = {
  modalVisible: PropTypes.any,
  handleModalVisible: PropTypes.any,
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
};
export default MobilePaymentChangeModal;
